import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verify the session via the HttpOnly cookie. The token is never exposed to JS.
  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/auth/verify', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = response.ok ? await response.json() : null;
      if (data?.valid && data.user) {
        setUser(data.user);
        setIsAuthenticated(true);
        return true;
      }
    } catch (error) {
      console.error('Session verification error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
    return false;
  }, []);

  // Restore session on mount
  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Login - redirect to Google OAuth
  const login = () => {
    window.location.href = '/auth/google';
  };

  // Logout - clear server session (revokes + clears cookie) then local state
  const logout = async () => {
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/resources/login';
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    refresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
